import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Chip,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy'
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import {
  fetchEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from '../store/slices/calendarSlice';
import { CalendarEvent, CalendarEventCreateInput } from '../store/slices/calendarSlice';
import { fetchTasks } from '../store/slices/tasksSlice';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import { useNavigate } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import EventIcon from '@mui/icons-material/Event';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

const Calendar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const { events, loading, error } = useSelector((state: RootState) => state.calendar);
  const { tasks } = useSelector((state: RootState) => state.tasks);
  const { user } = useSelector((state: RootState) => state.auth);
  const theme = useTheme();
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayViewOpen, setDayViewOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [formData, setFormData] = useState<CalendarEventCreateInput>({
    user_id: user?.user_id?.toString() || '',
    title: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    location: '',
    type: 'meeting',
    color: '#2563eb',
    all_day: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    if (user?.user_id) {
      dispatch(fetchEvents({
        userId: user.user_id.toString(),
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
      }));
    }
    // Re-fetch whenever the browsed month changes, so navigating months
    // reflects that month's actual server data instead of re-filtering
    // whatever the initial month's payload happened to contain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user, currentDate.getMonth(), currentDate.getFullYear()]);

  useEffect(() => {
    if (user?.user_id) {
      dispatch(fetchTasks(user.user_id.toString()));
    }
  }, [dispatch, user]);

  const handleOpenDialog = (event?: CalendarEvent) => {
    if (event) {
      setSelectedEvent(event);
      setFormData({
        user_id: user?.user_id?.toString() || '',
        title: event.title,
        description: event.description,
        start_date: event.start_date,
        end_date: event.end_date,
        location: event.location || '',
        type: event.type,
        color: event.color,
        all_day: event.all_day,
      });
    } else {
      setSelectedEvent(null);
      setFormData({
        user_id: user?.user_id?.toString() || '',
        title: '',
        description: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        location: '',
        type: 'meeting',
        color: '#2563eb',
        all_day: false,
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (selectedEvent) {
        await dispatch(updateEvent({
          id: selectedEvent.id,
          ...formData,
        }));
        setSnackbar({
          open: true,
          message: t('calendar.eventUpdated'),
          severity: 'success',
        });
      } else {
        await dispatch(createEvent(formData));
        setSnackbar({
          open: true,
          message: t('calendar.eventCreated'),
          severity: 'success',
        });
      }
      setOpenDialog(false);
      setSelectedEvent(null);
      setFormData({
        user_id: user?.user_id?.toString() || '',
        title: '',
        description: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        location: '',
        type: 'meeting',
        color: '#2563eb',
        all_day: false,
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('calendar.eventSavedFailed'),
        severity: 'error',
      });
    }
  };

  const handleDelete = async (eventId: number) => {
    const ok = await confirm(t('calendar.confirmDeleteEvent'));
    if (ok) {
      try {
        await dispatch(deleteEvent({ eventId, userId: user?.user_id?.toString() || '' }));
        setSnackbar({
          open: true,
          message: t('calendar.eventDeleted'),
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('calendar.eventDeleteFailed'),
          severity: 'error',
        });
      }
    }
  };

  const getEventColor = (type: string) => {
    const colors: Record<string, string> = {
      meeting: '#2563eb',
      hearing: '#dc2626',
      deadline: '#f97316',
      other: '#64748b',
    };
    return colors[type] || '#64748b';
  };

  const toDateStr = (date: Date) => {
    // Local calendar date, not UTC — date.toISOString() shifts to UTC and
    // can land on the wrong day depending on timezone.
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getEventsForDateStr = (dateStr: string) =>
    events.filter(event => event.start_date.startsWith(dateStr));

  const getTasksForDateStr = (dateStr: string) =>
    tasks.filter(task => task.due_date && task.due_date.startsWith(dateStr));

  const handleDayClick = (year: number, month: number, day: number) => {
    setSelectedDate(new Date(year, month, day));
    setDayViewOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography>{t('common.loading')}...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('navigation.calendar')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('calendar.addEvent')}
        </Button>
      </Box>

      {/* Calendar View */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {[
                t('calendar.sun'),
                t('calendar.mon'),
                t('calendar.tue'),
                t('calendar.wed'),
                t('calendar.thu'),
                t('calendar.fri'),
                t('calendar.sat'),
              ].map((day) => (
                <Box key={day} sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {day}
                  </Typography>
                </Box>
              ))}
              {(() => {
                const year = currentDate.getFullYear();
                const month = currentDate.getMonth();
                const firstDay = new Date(year, month, 1).getDay();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const days: (number | null)[] = [];
                
                for (let i = 0; i < firstDay; i++) days.push(null);
                for (let i = 1; i <= daysInMonth; i++) days.push(i);
                
                return days.map((day, index) => {
                  if (day === null) return <Box key={`empty-${index}`} />;
                  
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayEvents = getEventsForDateStr(dateStr);
                  const dayTasks = getTasksForDateStr(dateStr);
                  const isToday = dateStr === toDateStr(new Date());
                  
                  return (
                    <Box
                      key={day}
                      onClick={() => handleDayClick(year, month, day)}
                      sx={{
                        p: 1,
                        minHeight: 80,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: isToday ? theme.palette.primary.main + '08' : 'transparent',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: theme.palette.action.hover },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isToday ? 700 : 400,
                            color: isToday ? 'primary.main' : 'text.primary',
                            mb: 0.5
                          }}
                        >
                          {day}
                        </Typography>
                        {dayTasks.length > 0 && (
                          <TaskAltIcon sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 0.5 }} />
                        )}
                      </Box>
                      {dayEvents.slice(0, 2).map((event) => (
                        <Box
                          key={event.id}
                          sx={{
                            bgcolor: getEventColor(event.type) + '20',
                            color: getEventColor(event.type),
                            borderRadius: 0.5,
                            px: 0.5,
                            mb: 0.25,
                            borderLeft: `3px solid ${getEventColor(event.type)}`,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: getEventColor(event.type) + '30' },
                          }}
                          onClick={(e) => { e.stopPropagation(); handleOpenDialog(event); }}
                        >
                          <Typography variant="caption" noWrap sx={{ fontSize: '0.65rem' }}>
                            {event.title}
                          </Typography>
                        </Box>
                      ))}
                      {dayEvents.length > 2 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {t('calendar.moreEvents', { count: dayEvents.length - 2 })}
                        </Typography>
                      )}
                    </Box>
                  );
                });
              })()}
            </Box>
          </CardContent>
        </Card>

        {/* Selected Day Events Sidebar */}
        <Card sx={{ width: 300, flexShrink: 0 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {t('calendar.eventsForDate', { date: selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) })}
              </Typography>
              <Button size="small" onClick={() => setDayViewOpen(true)}>
                {t('calendar.viewDay', 'View day')}
              </Button>
            </Box>
            <Stack spacing={1.5}>
              {getEventsForDateStr(toDateStr(selectedDate)).map((event) => (
                  <Card key={event.id} variant="outlined" sx={{ cursor: 'pointer' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                          {event.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleOpenDialog(event)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(event.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {event.description}
                      </Typography>
                      <Chip
                        label={event.type}
                        size="small"
                        sx={{
                          mt: 1,
                          backgroundColor: getEventColor(event.type),
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    </CardContent>
                  </Card>
                ))}
              {getEventsForDateStr(toDateStr(selectedDate)).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('calendar.noEventsToday')}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Day Detail Dialog — events, tasks, and quick actions for the clicked date */}
      <Dialog open={dayViewOpen} onClose={() => setDayViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          <IconButton onClick={() => setDayViewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('calendar.title', 'Events')} ({getEventsForDateStr(toDateStr(selectedDate)).length})
          </Typography>
          {getEventsForDateStr(toDateStr(selectedDate)).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('calendar.noEventsToday')}
            </Typography>
          ) : (
            <List dense disablePadding sx={{ mb: 2 }}>
              {getEventsForDateStr(toDateStr(selectedDate)).map((event) => (
                <ListItem
                  key={event.id}
                  disableGutters
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => { setDayViewOpen(false); handleOpenDialog(event); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(event.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <EventIcon fontSize="small" sx={{ mr: 1.5, color: getEventColor(event.type) }} />
                  <ListItemText
                    primary={event.title}
                    secondary={new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('tasks.title', 'Tasks')} ({getTasksForDateStr(toDateStr(selectedDate)).length})
          </Typography>
          {getTasksForDateStr(toDateStr(selectedDate)).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('calendar.noTasksToday', 'No tasks due this day')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {getTasksForDateStr(toDateStr(selectedDate)).map((task) => (
                <ListItem
                  key={task.id}
                  disableGutters
                  sx={{ cursor: 'pointer' }}
                  onClick={() => { setDayViewOpen(false); navigate('/tasks'); }}
                >
                  <TaskAltIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                  <ListItemText primary={task.title} secondary={task.status} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              setDayViewOpen(false);
              handleOpenDialog();
              setFormData((prev) => ({
                ...prev,
                start_date: toDateStr(selectedDate),
                end_date: toDateStr(selectedDate),
              }));
            }}
          >
            {t('calendar.addEvent')}
          </Button>
          <Button onClick={() => navigate('/tasks')}>
            {t('tasks.addTask', 'Add Task')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Event Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedEvent ? t('calendar.editEvent') : t('calendar.addEvent')}
          <IconButton onClick={() => setOpenDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t('calendar.eventTitle')}
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <TextField
              label={t('common.description')}
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('calendar.startDate')}
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
              <TextField
                label={t('calendar.endDate')}
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </Box>
            <TextField
              select
              label={t('calendar.type')}
              fullWidth
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <MenuItem value="meeting">{t('calendar.typeMeeting')}</MenuItem>
              <MenuItem value="hearing">{t('calendar.typeHearing')}</MenuItem>
              <MenuItem value="deadline">{t('calendar.typeDeadline')}</MenuItem>
              <MenuItem value="other">{t('calendar.typeOther')}</MenuItem>
            </TextField>
            <TextField
              label={t('calendar.location')}
              fullWidth
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            />
            <TextField
              select
              label={t('calendar.color')}
              fullWidth
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            >
              <MenuItem value="#2563eb">{t('calendar.colorBlue')}</MenuItem>
              <MenuItem value="#dc2626">{t('calendar.colorRed')}</MenuItem>
              <MenuItem value="#f97316">{t('calendar.colorOrange')}</MenuItem>
              <MenuItem value="#64748b">{t('calendar.colorPurple')}</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedEvent ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Calendar;
